/** The "B2B Bot Groups" Setting, parsed. Pure: shared by the Settings
 *  validator (client + server) and the eager bots' group cycle
 *  (server/mogbot/eagerGroups). One group per line:
 *
 *    CardHoarder: CardhoarderBuyBot, CardhoarderBuyBot2, CardhoarderBuyBot3
 *
 *  Members are exact MTGO usernames (matched case-insensitively); blank
 *  lines and lines starting with # are ignored. */

export interface B2bGroup {
  name: string;
  /** Usernames as entered, in order: the order members are tried in. */
  members: string[];
}

export const parseB2bGroups = (text: string | null | undefined): { groups: B2bGroup[]; errors: string[] } => {
  const groups: B2bGroup[] = [];
  const errors: string[] = [];
  const seenGroup = new Set<string>();
  const seenMember = new Map<string, string>();
  (text ?? '').split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (!line || line.startsWith('#')) return;
    const at = line.indexOf(':');
    if (at < 0) { errors.push(`line ${i + 1}: expected "Group name: bot, bot"`); return; }
    const name = line.slice(0, at).trim();
    const members = line.slice(at + 1).split(/[,;]+/).map((m) => m.trim()).filter(Boolean);
    if (!name) { errors.push(`line ${i + 1}: the group has no name`); return; }
    if (!members.length) { errors.push(`line ${i + 1}: ${name} has no bots`); return; }
    if (seenGroup.has(name.toLowerCase())) { errors.push(`line ${i + 1}: ${name} is listed twice`); return; }
    seenGroup.add(name.toLowerCase());
    for (const m of members) {
      const k = m.toLowerCase();
      const dup = seenMember.get(k);
      if (dup) errors.push(`line ${i + 1}: ${m} is already in ${dup}`);
      else seenMember.set(k, name);
    }
    groups.push({ name, members });
  });
  return { groups, errors };
};

export const validateB2bGroups = (value: unknown): string | null => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value !== 'string') return 'Must be text: one group per line, "Group name: bot, bot"';
  const { errors } = parseB2bGroups(value);
  return errors.length ? errors.slice(0, 3).join('; ') + (errors.length > 3 ? `; and ${errors.length - 3} more` : '') : null;
};

/** The groups from the 2026-09-30 network analysis: the chains that
 *  consistently offer fair buy prices, as ticketed. */
export const DEFAULT_B2B_GROUPS_TEXT = [
  '4U Trading: ______________4',
  'Aether / Arcane: Aether_Bot_1, Aether_Bot_2, Aether_Bot_3, Aether_Bot_4, Arcane_Bot_1, Arcane_Bot_2, Arcane_Bot_3, Arcane_Bot_4',
  'Happy: Happy9',
  'Sphinx: SphinxBot, SphinxBot1',
  'Booster 4U: BOOSTER_4U',
  'Dojo Trade: _DojoTradeBuying, _DojoTradeBuying2, _DojoTradeBuying3, _DojoTradeBuying4, _DojoTradeBuying5, _DojoTradeBoosters',
  'CardHoarder: CardhoarderBuyBot, CardhoarderBuyBot2, CardhoarderBuyBot3, CardhoarderBuyBot4, CardhoarderBuyBot5',
  'CardBuying: CardBuyingBot, CardBuyingBot1, CardBuyingBot2, CardBuyingBot3, CardBuyingBot4, CardBuyingBot10, CardBuyingBot20, CardBuyingBot30, CardBuyingBot40',
  'Cardfiend: Cardfiend5',
  'Manatraders: Manatrader_bot_1, Manatraders_Bot_10, Manatraders_Bot_2, Manatraders_Bot_4, Manatraders_Bot_5, Manatraders_Bot_6, Manatraders_Bot_7, Manatraders_Bot_8, Manatraders_Bot_9',
  'MTGRunners: MTGRunners',
  'BoosterGod: BoosterGod, BoosterGod0, BoosterGod1, BoosterGod2, BoosterGod3, BoosterGod4, BoosterGod5, BoosterGod6, BoosterGod7',
  'Frankbb: TheBuyBot',
].join('\n');
